/* Catalog 3D Story engine.
   Scroll-driven waypoint choreography for a product GLB rendered on a
   fixed full-viewport Three.js stage behind the section content.
   Ported from the sofas-exclusive-catalog.html prototype:
   - each [data-c3d-waypoint] carries a keyframe {rx,ry,s,x,y,o}
   - scroll picks the segment between consecutive waypoints, eases the
     interpolation, and a rAF loop smooth-lerps the model to the target
   - a fixed rail mirrors waypoints as clickable dots with progress fills
   - an entrance pose animates to the first keyframe on load */
(function () {
  'use strict';

  function initCatalog3dStory(root) {
    if (!root || root.dataset.c3dBound) return;
    const stage = root.querySelector('[data-c3d-stage]');
    const canvas = root.querySelector('[data-c3d-canvas]');
    const rail = root.querySelector('[data-c3d-rail]');
    const sections = Array.from(root.querySelectorAll('[data-c3d-waypoint]'));
    if (sections.length === 0) return;

    root.dataset.c3dBound = 'true';
    let destroyed = false;
    let rafId = 0;
    const cleanup = [];
    const pendingRafs = new Set();
    const pendingTimeouts = new Set();
    const requestFrame = (callback) => {
      const id = requestAnimationFrame((time) => {
        pendingRafs.delete(id);
        if (!destroyed) callback(time);
      });
      pendingRafs.add(id);
      return id;
    };
    const schedule = (callback, delay) => {
      const id = setTimeout(() => {
        pendingTimeouts.delete(id);
        if (!destroyed) callback();
      }, delay);
      pendingTimeouts.add(id);
      return id;
    };
    /* Registered listener: removed by _c3dDestroy, so a re-init of the
       same root (theme editor / hot reload) can't double-bind. */
    const listen = (el, type, fn, opts) => {
      el.addEventListener(type, fn, opts);
      cleanup.push(() => el.removeEventListener(type, fn, opts));
    };
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* The product's Shopify-hosted Model3d GLB. If it's missing or fails to
     load the stage is dropped (c3d--no-model) and the section presents as
     a plain scrolling story. */
    const modelUrl = root.dataset.modelUrl || '';

    const DEG = Math.PI / 180;

    /* Keyframes: settings use degrees / percents; convert to the radians /
     fractions the engine works in. */
    const KEYS = sections.map((sec) => {
      let k = {};
      try {
        k = JSON.parse(sec.dataset.key || '{}');
      } catch (e) {
        /* leave defaults */
      }
      return {
        rx: (k.rx || 0) * DEG,
        ry: (k.ry || 0) * DEG,
        s: (k.s == null ? 100 : k.s) / 100,
        x: (k.x || 0) / 100,
        y: (k.y || 0) / 100,
        /* Floor opacity so gallery/specs waypoints stay visible; the old
           20–30% keys read as the model "disappearing" mid-story. */
        o: Math.max(0.7, (k.o == null ? 100 : k.o) / 100),
      };
    });

    /* ---------- scroll reveals (work with or without WebGL) ---------- */
    function revealCheck() {
      root.querySelectorAll('.c3d__fx:not(.is-in)').forEach((el) => {
        if (el.getBoundingClientRect().top < window.innerHeight * 0.86) {
          el.classList.add('is-in');
        }
      });
    }

    /* ---------- rail ---------- */
    const railDots = [];
    const railFills = [];
    if (rail) {
      sections.forEach((sec, i) => {
        if (i > 0) {
          const line = document.createElement('div');
          line.className = 'c3d__rail-line';
          line.innerHTML = '<i></i>';
          rail.appendChild(line);
          railFills.push(line.firstChild);
        }
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'c3d__rail-dot';
        dot.setAttribute('aria-label', sec.dataset.wpLabel || 'Section ' + (i + 1));
        dot.innerHTML = '<span class="c3d__rail-lbl">' + (sec.dataset.wpLabel || '') + '</span>';
        dot.addEventListener('click', () => {
          window.scrollTo({
            top: sec.getBoundingClientRect().top + window.scrollY,
            behavior: reduced ? 'auto' : 'smooth',
          });
        });
        rail.appendChild(dot);
        railDots.push(dot);
      });
    }

    /* ---------- WebGL stage ---------- */
    let renderer = null;
    let scene, camera, modelGroup, model;
    let modelAspect = 1;
    let heroFitWeight = 1;
    let viewportWidth = window.innerWidth;
    let viewportHeight = window.innerHeight;

    /* This adjusts only the hero waypoint's settled position on phones.
       Keep INTRO untouched: it is the separate off-screen load pose. */
    const MOBILE_HERO_MIN_Y_OFFSET = 0.18;
    const MOBILE_HERO_LANDSCAPE_Y_BONUS = 0.2;
    const INTRO = { rx: 1.05, ry: KEYS[0].ry - 2.9, s: 0.1, x: 0, y: -0.52, o: 0 };
    const INTRO_MS = 1600;
    const cur = Object.assign({}, INTRO);
    let target = Object.assign({}, KEYS[0]);
    let introStart = 0;
    let introActive = false;

    const lerp = (a, b, t) => a + (b - a) * t;
    const easeIO = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);

    function makeLights(s) {
      const brandWhite = new THREE.Color(
        getComputedStyle(document.documentElement).getPropertyValue('--catalog-white').trim(),
      );
      /* AUDIT-IGNORE: warm hemisphere/rim literals are calibrated WebGL
         lighting, not interface palette values. Exact brand white is read
         from the generated token because three.js cannot consume CSS vars. */
      s.add(new THREE.HemisphereLight(brandWhite, 0xcfc8bf, 0.95));
      const d = new THREE.DirectionalLight(brandWhite, 0.85);
      d.position.set(2, 4, 3);
      s.add(d);
      const d2 = new THREE.DirectionalLight(0xf3e2cd, 0.35);
      d2.position.set(-3, 1.5, -2);
      s.add(d2);
    }

    /* Every model source failed (or none existed): drop the WebGL stage and
     present as a plain scrolling story. */
    function dropStage() {
      root.classList.add('c3d--no-model');
      if (rail) rail.classList.add('is-visible');
    }

    function initGL() {
      if (!stage || !canvas || !window.THREE || !THREE.GLTFLoader || !modelUrl) {
        return false;
      }
      try {
        renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true });
      } catch (e) {
        return false;
      }
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.outputEncoding = THREE.sRGBEncoding;
      scene = new THREE.Scene();
      camera = new THREE.PerspectiveCamera(32, viewportWidth / viewportHeight, 0.01, 10);
      camera.position.set(0, 0.16, 1.55);
      camera.lookAt(0, 0, 0);
      makeLights(scene);
      modelGroup = new THREE.Group();
      scene.add(modelGroup);

      const sizeIt = () => {
        /* iOS pull-to-refresh / collapsing browser chrome changes the visual
           viewport without reliably firing window.resize. Use its dimensions
           so the CSS-sized canvas and WebGL drawing buffer never disagree
           and stretch the product model. */
        const visualViewport = window.visualViewport;
        viewportWidth = Math.round(visualViewport ? visualViewport.width : window.innerWidth);
        viewportHeight = Math.round(visualViewport ? visualViewport.height : window.innerHeight);
        renderer.setSize(viewportWidth, viewportHeight, false);
        camera.aspect = viewportWidth / viewportHeight;
        camera.updateProjectionMatrix();
      };
      sizeIt();
      window.addEventListener('resize', sizeIt);
      cleanup.push(() => window.removeEventListener('resize', sizeIt));
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', sizeIt);
        cleanup.push(() => window.visualViewport.removeEventListener('resize', sizeIt));
      }

      /* Adopt a freshly loaded GLB scene as the stage model: center, then
       normalize — catalog GLBs are real-world scale (a sectional is ~2.7m
       wide), so fit the largest dimension to a fixed stage size the
       keyframe scales multiply against. Real-world metrics are kept on
       userData for the AR session, which needs true scale back. */
      function adoptModel(gltfScene) {
        const s = gltfScene;
        const box = new THREE.Box3().setFromObject(s);
        const center = box.getCenter(new THREE.Vector3());
        s.position.sub(center);
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z) || 1;
        const norm = 0.62 / maxDim;
        s.scale.setScalar(norm);
        /* The hero's portrait-screen width limit responds to the model's
           frontal proportions. Wider/landscape furniture keeps nearly all
           of its desktop presence; square and taller forms get a little
           more room above and below the model on a tall phone. */
        modelAspect = size.y > 0 ? size.x / size.y : 1;
        s.userData.c3dCenter = center.clone(); /* raw meters */
        s.userData.c3dMinY = box.min.y; /* raw meters */
        if (model) modelGroup.remove(model);
        model = s;
        modelGroup.add(s);
      }

      new THREE.GLTFLoader().load(
        modelUrl,
        (gltf) => {
          if (destroyed) return;
          adoptModel(gltf.scene);
          stage.classList.add('is-ready');
          if (rail) rail.classList.add('is-visible');
          arSupportCheck();
          startIntro();
          onScroll();
        },
        undefined,
        (err) => {
          if (destroyed) return;
          if (window.console) {
            console.warn('catalog-3d-story: GLB load failed for ' + modelUrl, err);
          }
          dropStage();
        },
      );

      /* ---------- AR: "view in your room" ----------
       Native OS AR — the same viewers Shopify's <model-viewer> launches,
       so it behaves like the standard product page and covers every
       AR-capable mobile device:
       • iOS (Safari/WebKit): AR Quick Look via a programmatic <a rel="ar">
         to the product's Shopify-hosted USDZ (auto-generated from the GLB).
       • Android: Google Scene Viewer via an intent:// URL to the GLB, with
         the product page as the browser fallback.
       The button starts disabled; arSupportCheck() enables it once a model
       is available and the device supports one of the two paths. */
      const arButtons = Array.from(root.querySelectorAll('[data-c3d-ar]'));
      let arMode = null; /* 'quicklook' | 'scene-viewer' | null */
      const usdzUrl = root.dataset.modelUsdz || '';

      /* iOS AR Quick Look support: the browser recognizes <a rel="ar">. */
      const quickLookProbe = document.createElement('a');
      const hasQuickLook =
        quickLookProbe.relList && quickLookProbe.relList.supports && quickLookProbe.relList.supports('ar');
      const isAndroid = /android/i.test(navigator.userAgent);

      function enableArButtons() {
        arButtons.forEach((b) => {
          b.disabled = false;
          b.removeAttribute('title');
        });
        root.querySelectorAll('[data-c3d-ar-note]').forEach((n) => n.remove());
      }

      function arSupportCheck() {
        if (!model || arButtons.length === 0) return;

        /* iOS AR Quick Look (needs a USDZ). */
        if (hasQuickLook && usdzUrl) {
          arMode = 'quicklook';
          enableArButtons();
          return;
        }

        /* Android Scene Viewer (needs the GLB). */
        if (isAndroid && modelUrl) {
          arMode = 'scene-viewer';
          enableArButtons();
        }
      }

      /* ---- iOS: AR Quick Look ---- */
      function startQuickLook() {
        if (!usdzUrl) return;
        const a = document.createElement('a');
        a.rel = 'ar';
        a.href = usdzUrl;
        /* AR Quick Look requires an <img> child to activate. */
        a.appendChild(document.createElement('img'));
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        /* Clean up after the link is activated. */
        requestFrame(() => {
          if (a.parentNode) a.parentNode.removeChild(a);
        });
      }

      /* ---- Android: Google Scene Viewer ---- */
      function startSceneViewer() {
        if (!modelUrl) return;
        const glb = new URL(modelUrl, window.location.href).href;
        const fallback = window.location.href;
        const query = 'file=' + encodeURIComponent(glb) + '&mode=ar_preferred&resizable=false';
        const intent =
          'intent://arvr.google.com/scene-viewer/1.0?' +
          query +
          '#Intent;scheme=https;package=com.google.android.googlequicksearchbox;' +
          'action=android.intent.action.VIEW;' +
          'S.browser_fallback_url=' +
          encodeURIComponent(fallback) +
          ';end;';
        window.location.href = intent;
      }

      function handleAR() {
        if (arMode === 'quicklook') startQuickLook();
        else if (arMode === 'scene-viewer') startSceneViewer();
      }

      arButtons.forEach((btn) => listen(btn, 'click', handleAR));

      function loop() {
        rafId = requestFrame(loop);
        tick();
      }
      loop();
      return true;
    }

    function startIntro() {
      if (reduced) {
        Object.assign(cur, KEYS[0]);
        introActive = false;
        return;
      }
      Object.assign(cur, INTRO);
      introStart = performance.now();
      introActive = true;
    }

    function tick() {
      if (!model || !renderer) return;
      if (introActive) {
        const e = easeOut(Math.min(1, (performance.now() - introStart) / INTRO_MS));
        for (const p of ['rx', 'ry', 's', 'x', 'y', 'o']) cur[p] = lerp(INTRO[p], target[p], e);
        if (e >= 1) introActive = false;
      } else {
        const k = reduced ? 1 : 0.085;
        for (const p of ['rx', 'ry', 's', 'x', 'y', 'o']) cur[p] = lerp(cur[p], target[p], k);
      }
      modelGroup.rotation.x = cur.rx;
      modelGroup.rotation.y = cur.ry + (reduced ? 0 : Math.sin(performance.now() * 0.0004) * 0.03);
      const halfH = Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)) * camera.position.z;
      const halfW = halfH * camera.aspect;
      /* Narrow viewports: clamp the normalized model (0.62 world units at
       scale 1) into the visible frustum so keyframes tuned on desktop
       don't clip the model off the stage edges on mobile. In the hero,
       tall portrait screens reserve progressively more vertical breathing
       room for square/tall models; landscape models are largely spared. */
      const isNarrow = viewportWidth < 900;
      const viewportPortraitness = Math.min(1, Math.max(0, viewportHeight / viewportWidth - 1));
      const landscapeProtection = Math.min(0.85, Math.max(0, (modelAspect - 1) / 1.5));
      const heroPortraitReduction = 0.22 * viewportPortraitness * (1 - landscapeProtection) * heroFitWeight;
      const fit = isNarrow ? Math.min(1, (Math.min(halfW, halfH) * 2 * (0.92 - heroPortraitReduction)) / 0.62) : 1;
      modelGroup.scale.setScalar(cur.s * fit);
      /* Mobile hero: use a centered, elevated product pose over the
         details. Later waypoints retain their authored horizontal paths. */
      const isMobileHero = isNarrow && heroFitWeight > 0;
      modelGroup.position.set(
        isMobileHero ? 0 : cur.x * halfW * (isNarrow ? 0.3 : 1),
        cur.y * halfH,
        0,
      );
      canvas.style.opacity = cur.o;
      renderer.render(scene, camera);
    }

    /* ---------- scroll driver ---------- */
    function sectionTop(sec) {
      return sec.getBoundingClientRect().top + window.scrollY;
    }

    function onScroll() {
      const st = window.scrollY;
      const vh = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      const rootBottom = root.getBoundingClientRect().bottom;
      /* Keep the model fixed behind every part of the story and its handoff
         into related products. Releasing at 35% of the viewport hid the
         model while the final specifications were still on screen. */
      root.classList.toggle('c3d--past', rootBottom < 0);
      let seg = 0;
      let t = 0;
      for (let i = 0; i < sections.length; i++) {
        const a = sectionTop(sections[i]);
        const b = sections[i + 1] ? sectionTop(sections[i + 1]) : a + vh;
        if (st >= a - vh * 0.5 && st < b - vh * 0.5) {
          seg = i;
          t = Math.min(1, Math.max(0, (st - (a - vh * 0.5)) / (b - a)));
          break;
        }
        if (st >= b - vh * 0.5) {
          seg = i;
          t = 1;
        }
      }
      const A = KEYS[Math.min(seg, KEYS.length - 1)];
      const B = KEYS[Math.min(seg + 1, KEYS.length - 1)];
      const e = easeIO(t);
      const isMobile = viewportWidth < 900;
      /* Wider furniture needs a little more lift to clear the hero copy;
         portrait/taller pieces (chairs) remain lower for a balanced frame. */
      const mobileLandscapeWeight = Math.min(0.85, Math.max(0, (modelAspect - 1) / 1.5));
      const mobileHeroYOffset =
        MOBILE_HERO_MIN_Y_OFFSET + MOBILE_HERO_LANDSCAPE_Y_BONUS * mobileLandscapeWeight;
      target = {
        rx: lerp(A.rx, B.rx, e),
        ry: lerp(A.ry, B.ry, e),
        s: lerp(A.s, B.s, e),
        x: lerp(A.x, B.x, e),
        /* Desktop uses authored vertical waypoints. On mobile, holding
           later poses at center avoids the hero → center → high → center
           bounce while retaining one gentle descent from the elevated hero. */
        y: isMobile
          ? mobileHeroYOffset * (seg === 0 ? 1 - e : 0)
          : lerp(A.y, B.y, e),
        o: lerp(A.o, B.o, e),
      };
      heroFitWeight = seg === 0 ? 1 - e : 0;
      let active = seg + (t > 0.55 ? 1 : 0);
      active = Math.min(active, railDots.length - 1);
      railDots.forEach((d, i) => d.classList.toggle('is-active', i === active));
      railFills.forEach((f, i) => {
        f.style.height = (i < seg ? 100 : i === seg ? t * 100 : 0) + '%';
      });
      revealCheck();
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    cleanup.push(() => window.removeEventListener('scroll', onScroll));
    cleanup.push(() => window.removeEventListener('resize', onScroll));
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', onScroll);
      cleanup.push(() => window.visualViewport.removeEventListener('resize', onScroll));
    }

    /* ---------- boot ---------- */
    const glOk = initGL();
    if (!glOk) {
      /* No WebGL / no model source: plain scrolling story with rail + reveals. */
      dropStage();
    }
    onScroll();
    revealCheck();
    /* Entrance choreography for the hero (double rAF so initial styles
     are committed before the transition classes flip). */
    requestFrame(() => {
      requestFrame(() => root.classList.add('is-entered'));
    });

    /* ---------- gallery lightbox ---------- */
    (function initGalleryLightbox() {
      const lb = root.querySelector('[data-c3d-lightbox]');
      const lbImg = root.querySelector('[data-c3d-lb-img]');
      const lbCaption = root.querySelector('[data-c3d-lb-caption]');
      const lbCounter = root.querySelector('[data-c3d-lb-counter]');
      const btnClose = root.querySelector('[data-c3d-lb-close]');
      const btnPrev = root.querySelector('[data-c3d-lb-prev]');
      const btnNext = root.querySelector('[data-c3d-lb-next]');
      const strip = root.querySelector('[data-c3d-thumb-strip]');
      const modalStrip = root.querySelector('[data-c3d-lb-thumbs]');
      const loading = root.querySelector('[data-c3d-lb-loading]');
      const galGrid = root.querySelector('[data-c3d-gal-grid]');

      if (!lb || !lbImg) return;

      let images = [];
      try {
        images = JSON.parse(root.querySelector('[data-c3d-gallery-images]').textContent);
      } catch (_) {
        return;
      }
      if (images.length === 0) return;

      let current = 0;
      let triggerEl = null;

      function setLoading(isLoading) {
        if (loading) loading.hidden = !isLoading;
      }

      function preloadAdjacentImages() {
        [current - 1, current + 1].forEach((candidate) => {
          const image = images[(candidate + images.length) % images.length];
          if (!image?.src) return;
          const preload = new Image();
          preload.src = image.src;
        });
      }

      function show(idx) {
        current = ((idx % images.length) + images.length) % images.length;
        const img = images[current];
        setLoading(true);
        lbImg.onload = function () {
          setLoading(false);
        };
        lbImg.onerror = function () {
          setLoading(false);
        };
        lbImg.src = img.src;
        lbImg.alt = img.alt || '';
        lbCaption.textContent = img.alt || '';
        lbCounter.textContent = current + 1 + ' / ' + images.length;
        if (lbImg.complete) setLoading(false);

        if (strip) {
          strip.querySelectorAll('.c3d__thumb').forEach((t, i) => {
            t.classList.toggle('is-active', i === current);
          });
          const activeThumb = strip.children[current];
          if (activeThumb) {
            activeThumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
          }
        }
        if (modalStrip) {
          modalStrip.querySelectorAll('[data-c3d-lb-thumb-idx]').forEach((thumb) => {
            const isActive = parseInt(thumb.dataset.c3dLbThumbIdx, 10) === current;
            thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
            if (isActive) thumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
          });
        }
        preloadAdjacentImages();
      }

      function open(idx, trigger) {
        triggerEl = trigger || null;
        show(idx);
        lb.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        btnClose.focus();
      }

      function close() {
        lb.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        if (triggerEl) {
          triggerEl.focus();
          triggerEl = null;
        }
      }

      /* Thumbnail strip clicks */
      if (strip) {
        listen(strip, 'click', function (e) {
          const thumb = e.target.closest('[data-c3d-thumb-idx]');
          if (!thumb) return;
          open(parseInt(thumb.dataset.c3dThumbIdx, 10), thumb);
        });
      }

      if (modalStrip) {
        listen(modalStrip, 'click', function (e) {
          const thumb = e.target.closest('[data-c3d-lb-thumb-idx]');
          if (!thumb) return;
          show(parseInt(thumb.dataset.c3dLbThumbIdx, 10));
        });
      }

      /* Grid image clicks */
      if (galGrid) {
        listen(galGrid, 'click', function (e) {
          const fig = e.target.closest('figure');
          if (!fig) return;
          const figures = Array.from(galGrid.querySelectorAll('figure'));
          const idx = figures.indexOf(fig);
          if (idx >= 0) open(idx, fig);
        });
        galGrid.style.cursor = 'pointer';
      }

      listen(btnClose, 'click', close);
      listen(btnPrev, 'click', function () {
        show(current - 1);
      });
      listen(btnNext, 'click', function () {
        show(current + 1);
      });

      /* Backdrop click to close */
      listen(lb, 'click', function (e) {
        if (e.target === lb) close();
      });

      /* Keyboard navigation */
      function onLightboxKeydown(e) {
        if (lb.getAttribute('aria-hidden') !== 'false') return;
        switch (e.key) {
          case 'Escape':
            close();
            break;
          case 'ArrowLeft':
            show(current - 1);
            e.preventDefault();
            break;
          case 'ArrowRight':
            show(current + 1);
            e.preventDefault();
            break;
        }
      }
      document.addEventListener('keydown', onLightboxKeydown);
      cleanup.push(() => document.removeEventListener('keydown', onLightboxKeydown));
      cleanup.push(() => {
        if (lb.getAttribute('aria-hidden') === 'false') document.body.style.overflow = '';
      });

      /* Touch swipe */
      let touchStart = null;
      listen(
        lb,
        'touchstart',
        function (e) {
          if (e.target.closest('button')) return;
          touchStart = {
            x: e.changedTouches[0].clientX,
            y: e.changedTouches[0].clientY,
          };
        },
        { passive: true },
      );
      listen(
        lb,
        'touchend',
        function (e) {
          if (!touchStart) return;
          const dx = e.changedTouches[0].clientX - touchStart.x;
          const dy = e.changedTouches[0].clientY - touchStart.y;
          touchStart = null;
          if (e.target.closest('button')) return;
          if (dy > 80 && dy > Math.abs(dx)) {
            close();
            return;
          }
          if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
            if (dx > 0) show(current - 1);
            else show(current + 1);
          }
        },
        { passive: true },
      );
      listen(
        lb,
        'touchcancel',
        function () {
          touchStart = null;
        },
        { passive: true },
      );

      /* Thumbnail strip overflow fade indicators */
      if (strip) {
        const wrap = strip.closest('.c3d__thumb-strip-wrap');
        function checkOverflow() {
          if (!wrap) return;
          const sl = strip.scrollLeft;
          const sw = strip.scrollWidth;
          const cw = strip.clientWidth;
          wrap.classList.toggle('has-overflow-left', sl > 4);
          wrap.classList.toggle('has-overflow-right', sl + cw < sw - 4);
        }
        listen(strip, 'scroll', checkOverflow, { passive: true });
        listen(window, 'resize', checkOverflow);
        schedule(checkOverflow, 200);
      }
    })();

    root._c3dDestroy = function () {
      if (destroyed) return;
      destroyed = true;
      cancelAnimationFrame(rafId);
      pendingRafs.forEach((id) => cancelAnimationFrame(id));
      pendingRafs.clear();
      pendingTimeouts.forEach((id) => clearTimeout(id));
      pendingTimeouts.clear();
      cleanup.forEach((dispose) => dispose());
      cleanup.length = 0;
      if (model) {
        model.traverse((node) => {
          if (node.geometry) node.geometry.dispose();
          const materials = Array.isArray(node.material) ? node.material : node.material ? [node.material] : [];
          materials.forEach((material) => {
            // Dispose every texture slot (normal/roughness/ao/emissive…),
            // not just the base color map.
            for (const key in material) {
              if (material[key] && material[key].isTexture) material[key].dispose();
            }
            material.dispose();
          });
        });
      }
      if (renderer) {
        try {
          renderer.dispose();
          renderer.forceContextLoss && renderer.forceContextLoss();
        } catch (_) {
          /* The browser may already have released the context. */
        }
      }
      // Rail dots/lines are rebuilt by init; leaving the old ones behind
      // would duplicate them (and their click listeners) on a re-init of
      // the same root (theme editor / hot reload).
      if (rail) {
        rail.innerHTML = '';
        rail.classList.remove('is-visible');
      }
      root.classList.remove('is-entered');
      delete root.dataset.c3dBound;
      delete root._c3dDestroy;
    };
  }

  function boot() {
    document.querySelectorAll('[data-catalog-3d-story]').forEach(initCatalog3dStory);
  }

  if (window._catalog3dStoryLifecycleBound) {
    boot();
  } else {
    window._catalog3dStoryLifecycleBound = true;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }

    document.addEventListener('shopify:section:load', function (event) {
      const root = event.target.matches('[data-catalog-3d-story]')
        ? event.target
        : event.target.querySelector('[data-catalog-3d-story]');
      if (root) initCatalog3dStory(root);
    });
    document.addEventListener('shopify:section:unload', function (event) {
      const root = event.target.matches('[data-catalog-3d-story]')
        ? event.target
        : event.target.querySelector('[data-catalog-3d-story]');
      if (root && root._c3dDestroy) root._c3dDestroy();
    });
  }
})();
